import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  DocumentIcon,
  FolderIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useSearchStore } from '../../stores/searchStore';
import clsx from 'clsx';

export function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    query,
    setQuery,
    results,
    suggestions,
    isSearching,
    isOpen,
    openSearch,
    closeSearch,
    search,
  } = useSearchStore();

  // Keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) {
          closeSearch();
        } else {
          openSearch();
        }
      }
      if (e.key === 'Escape' && isOpen) {
        closeSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, openSearch, closeSearch]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.length >= 2) {
      search(query);
    }
  };

  const handleFileClick = (_fileId: string, folderId: string | null) => {
    closeSearch();
    if (folderId) {
      navigate(`/folder/${folderId}`);
    } else {
      navigate('/');
    }
  };

  const handleClientClick = (clientId: string) => {
    closeSearch();
    navigate(`/clients/${clientId}`);
  };

  const handleFolderClick = (folderId: string) => {
    closeSearch();
    navigate(`/folder/${folderId}`);
  };

  return (
    <>
      {/* Search trigger button */}
      <button
        onClick={openSearch}
        className={clsx(
          'flex items-center gap-2 px-3 py-2 rounded-lg',
          'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400',
          'hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors',
          'text-sm'
        )}
      >
        <MagnifyingGlassIcon className="h-4 w-4" />
        <span className="hidden sm:inline">Buscar...</span>
        <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-gray-200 dark:bg-slate-700 rounded">
          <span className="text-[10px]">⌘</span>K
        </kbd>
      </button>

      {/* Search modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeSearch}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              className={clsx(
                'fixed left-1/2 top-[20%] -translate-x-1/2 w-full max-w-2xl',
                'bg-white dark:bg-slate-800 rounded-xl shadow-2xl',
                'border border-gray-200 dark:border-slate-700',
                'z-50 overflow-hidden'
              )}
            >
              {/* Search input */}
              <form onSubmit={handleSubmit} className="relative">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar archivos, carpetas o clientes..."
                  className={clsx(
                    'w-full py-4 pl-12 pr-12 text-lg',
                    'bg-transparent border-b border-gray-200 dark:border-slate-700',
                    'text-gray-900 dark:text-white placeholder-gray-400',
                    'focus:outline-none'
                  )}
                />
                <button
                  type="button"
                  onClick={closeSearch}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </form>

              {/* Results */}
              <div className="max-h-[400px] overflow-y-auto">
                {isSearching ? (
                  <div className="p-8 text-center text-gray-500">Buscando...</div>
                ) : results ? (
                  <div className="p-2">
                    {/* Files */}
                    {results.files.length > 0 && (
                      <div className="mb-4">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                          Archivos
                        </div>
                        {results.files.map((file) => (
                          <button
                            key={file.id}
                            onClick={() => handleFileClick(file.id, file.folderId)}
                            className={clsx(
                              'w-full flex items-center gap-3 px-3 py-2 rounded-lg',
                              'hover:bg-gray-100 dark:hover:bg-slate-700 text-left'
                            )}
                          >
                            <DocumentIcon className="h-5 w-5 text-gray-400" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {file.originalName}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Folders */}
                    {results.folders.length > 0 && (
                      <div className="mb-4">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                          Carpetas
                        </div>
                        {results.folders.map((folder) => (
                          <button
                            key={folder.id}
                            onClick={() => handleFolderClick(folder.id)}
                            className={clsx(
                              'w-full flex items-center gap-3 px-3 py-2 rounded-lg',
                              'hover:bg-gray-100 dark:hover:bg-slate-700 text-left'
                            )}
                          >
                            <FolderIcon className="h-5 w-5 text-yellow-500" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {folder.name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">{folder.path}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Clients */}
                    {results.clients.length > 0 && (
                      <div>
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                          Clientes
                        </div>
                        {results.clients.map((client) => (
                          <button
                            key={client.id}
                            onClick={() => handleClientClick(client.id)}
                            className={clsx(
                              'w-full flex items-center gap-3 px-3 py-2 rounded-lg',
                              'hover:bg-gray-100 dark:hover:bg-slate-700 text-left'
                            )}
                          >
                            <UserGroupIcon className="h-5 w-5 text-primary-500" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {client.name}
                              </p>
                              {client.company && (
                                <p className="text-xs text-gray-500 truncate">{client.company}</p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* No results */}
                    {results.files.length === 0 &&
                      results.folders.length === 0 &&
                      results.clients.length === 0 && (
                        <div className="p-8 text-center text-gray-500">
                          No se encontraron resultados para "{query}"
                        </div>
                      )}
                  </div>
                ) : suggestions.length > 0 ? (
                  <div className="p-2">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                      Sugerencias
                    </div>
                    {suggestions.map((suggestion, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setQuery(suggestion.name);
                          search(suggestion.name);
                        }}
                        className={clsx(
                          'w-full flex items-center gap-3 px-3 py-2 rounded-lg',
                          'hover:bg-gray-100 dark:hover:bg-slate-700 text-left'
                        )}
                      >
                        {suggestion.type === 'file' ? (
                          <DocumentIcon className="h-4 w-4 text-gray-400" />
                        ) : (
                          <UserGroupIcon className="h-4 w-4 text-gray-400" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-slate-300">
                          {suggestion.name}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : query.length > 0 && query.length < 2 ? (
                  <div className="p-8 text-center text-gray-500">
                    Escribe al menos 2 caracteres para buscar
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    Escribe para buscar archivos, carpetas o clientes
                  </div>
                )}
              </div>

              {/* Footer hint */}
              <div className="px-4 py-3 border-t border-gray-200 dark:border-slate-700 text-xs text-gray-500">
                <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-slate-700 rounded">Enter</kbd> para
                buscar · <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-slate-700 rounded">Esc</kbd>{' '}
                para cerrar
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
